// src/pages/business/services/ServiceDetails.tsx
// Web adaptation of mobile ServiceDetailScreen - Enhanced service viewing experience

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Edit, Clock, Tag, Star, ChevronLeft, ChevronRight,
  Loader2, AlertCircle, Play, Image as ImageIcon, Video,
  MapPin, Link as LinkIcon, Package
} from 'lucide-react';
import apiClient from '@/services/apiClient';
import { getImageUrl } from '@/config/media';
import { useToast } from '@/contexts/ToastContext';

interface MediaItem {
  id: string;
  url: string;
  type: 'image' | 'video';
}

interface Addon {
  name: string;
  price: number;
}

interface Package {
  name: string;
  sessions: number;
  price: number;
}

interface Service {
  id: string;
  name: string;
  description?: string;
  price: number;
  duration_minutes?: number;
  category?: string;
  delivery_mode?: 'in_person' | 'online' | 'hybrid' | 'mobile';
  studio_location?: string;
  virtual_meeting_link?: string;
  is_active: boolean;
  is_featured: boolean;
  tags?: string[];
  media_urls?: { items: MediaItem[] };
  addons?: Addon[];
  packages?: Package[];
  rating?: number;
  reviews_count?: number;
  business_id: string;
  created_at: string;
  updated_at: string;
}

const deliveryModeLabels: Record<string, string> = {
  in_person: 'Available at our location',
  online: 'Online only',
  hybrid: 'Available online or in-person',
  mobile: 'I travel to clients',
};

const ServiceDetails: React.FC = () => {
  const { businessId, serviceId } = useParams<{ businessId: string; serviceId: string }>();
  const navigate = useNavigate();
  const { error: toastError } = useToast();

  const [service, setService] = useState<Service | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchService();
  }, [businessId, serviceId]);

  const fetchService = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await apiClient.get(`/businesses/services/by-id/${serviceId}`);
      setService(data);
    } catch (err: any) {
      console.error('Failed to fetch service details:', err);
      setError(err.response?.data?.error || 'Failed to load service details');
    } finally {
      setLoading(false);
    }
  };

  const media = service?.media_urls?.items || [];

  const nextMedia = () => {
    if (media.length > 0) {
      setCurrentMediaIndex((prev) => (prev + 1) % media.length);
    }
  };

  const prevMedia = () => {
    if (media.length > 0) {
      setCurrentMediaIndex((prev) => (prev - 1 + media.length) % media.length);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-light-bg dark:bg-dark-bg flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 size={40} className="animate-spin" style={{ color: '#4F46E5' }} />
          <p className="text-sm text-light-subtle dark:text-dark-subtle">Loading service details...</p>
        </div>
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="min-h-screen bg-light-bg dark:bg-dark-bg flex items-center justify-center">
        <div className="flex flex-col items-center gap-4 text-center p-8">
          <AlertCircle size={48} style={{ color: '#EF4444' }} />
          <h2 className="text-xl font-bold text-light-text dark:text-dark-text">Service Not Found</h2>
          <p className="text-light-subtle dark:text-dark-subtle">
            {error || 'The requested service could not be found.'}
          </p>
          <button
            onClick={() => navigate(`/business/${businessId}/services`)}
            className="mt-4 px-6 py-3 rounded-xl text-white font-semibold transition-colors"
            style={{ backgroundColor: '#4F46E5' }}
          >
            Go Back to Services
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-light-bg dark:bg-dark-bg pb-24">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border">
        <div className="max-w-5xl mx-auto flex items-center justify-between px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(`/business/${businessId}/services`)}
              className="p-2 rounded-lg hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
            >
              <ArrowLeft size={24} style={{ color: '#4F46E5' }} />
            </button>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-lg text-light-text dark:text-dark-text truncate">{service.name}</p>
              {service.category && (
                <p className="text-xs text-light-subtle dark:text-dark-subtle">{service.category}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {service.is_featured && (
              <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400">
                <Star size={12} fill="currentColor" />
                Featured
              </span>
            )}
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
              service.is_active 
                ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400' 
                : 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
            }`}>
              {service.is_active ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Media Carousel */}
        {media.length > 0 && (
          <div className="bg-light-card dark:bg-dark-card rounded-xl overflow-hidden shadow-sm relative">
            <div className="relative aspect-video">
              {media[currentMediaIndex].type === 'video' ? (
                <video
                  src={getImageUrl(media[currentMediaIndex].url)}
                  className="w-full h-full object-cover"
                  controls
                />
              ) : (
                <img
                  src={getImageUrl(media[currentMediaIndex].url)}
                  alt={`${service.name} - Image ${currentMediaIndex + 1}`}
                  className="w-full h-full object-cover"
                />
              )}
              
              {/* Navigation arrows */}
              {media.length > 1 && (
                <>
                  <button
                    onClick={prevMedia}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 flex items-center justify-center hover:bg-black/70 transition-colors"
                  >
                    <ChevronLeft size={20} className="text-white" />
                  </button>
                  <button
                    onClick={nextMedia}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 flex items-center justify-center hover:bg-black/70 transition-colors"
                  >
                    <ChevronRight size={20} className="text-white" />
                  </button>
                </>
              )}

              {/* Media counter */}
              {media.length > 1 && (
                <div className="absolute bottom-4 right-4 px-3 py-1 rounded-full bg-black/50 text-white text-xs">
                  {currentMediaIndex + 1} / {media.length}
                </div>
              )}
            </div>

            {/* Thumbnails */}
            {media.length > 1 && (
              <div className="flex gap-2 p-4 overflow-x-auto">
                {media.map((item, index) => (
                  <button
                    key={item.id}
                    onClick={() => setCurrentMediaIndex(index)}
                    className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                      index === currentMediaIndex 
                        ? 'border-primary' 
                        : 'border-transparent opacity-60 hover:opacity-100'
                    }`}
                  >
                    {item.type === 'video' ? (
                      <div className="relative w-full h-full">
                        <Video size={16} className="absolute top-1 right-1 text-white" />
                        <img
                          src={getImageUrl(item.url)}
                          alt={`Thumbnail ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <img
                        src={getImageUrl(item.url)}
                        alt={`Thumbnail ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Pricing & Basic Info */}
        <div className="bg-light-card dark:bg-dark-card rounded-xl p-5 shadow-sm">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-2xl font-bold text-light-text dark:text-dark-text">{service.name}</h2>
              {service.rating !== undefined && service.rating > 0 && (
                <div className="flex items-center gap-2 mt-2">
                  <div className="flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        size={14}
                        fill={star <= Math.round(service.rating || 0) ? '#FFD700' : 'none'}
                        stroke={star <= Math.round(service.rating || 0) ? '#FFD700' : '#CBD5E1'}
                        className={star <= Math.round(service.rating || 0) ? 'text-yellow-400' : ''}
                      />
                    ))}
                  </div>
                  <span className="text-sm font-semibold text-light-text dark:text-dark-text">
                    {service.rating?.toFixed(1)}
                  </span>
                  <span className="text-xs text-light-subtle dark:text-dark-subtle">
                    ({service.reviews_count || 0} reviews)
                  </span>
                </div>
              )}
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold" style={{ color: '#4F46E5' }}>
                KES {service.price.toLocaleString()}
              </p>
              {service.duration_minutes && (
                <p className="text-sm text-light-subtle dark:text-dark-subtle flex items-center justify-end gap-1 mt-1">
                  <Clock size={14} />
                  {service.duration_minutes} min
                </p>
              )}
            </div>
          </div>

          {service.description && (
            <div className="pt-4 border-t border-light-border dark:border-dark-border">
              <h4 className="text-sm font-semibold text-light-text dark:text-dark-text mb-2">Description</h4>
              <p className="text-sm text-light-subtle dark:text-dark-subtle whitespace-pre-wrap leading-relaxed">
                {service.description}
              </p>
            </div>
          )}
        </div>

        {/* Delivery Mode */}
        {service.delivery_mode && (
          <div className="bg-light-card dark:bg-dark-card rounded-xl p-5 shadow-sm">
            <h3 className="font-semibold text-sm mb-4 text-light-text dark:text-dark-text">Delivery Options</h3>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(79,70,229,0.12)' }}>
                <MapPin size={18} style={{ color: '#4F46E5' }} />
              </div>
              <div>
                <p className="font-medium text-sm text-light-text dark:text-dark-text">
                  {deliveryModeLabels[service.delivery_mode]}
                </p>
                <p className="text-xs text-light-subtle dark:text-dark-subtle">Service Delivery Mode</p>
              </div>
            </div>

            {service.studio_location && (
              <div className="flex items-center gap-3 p-3 bg-light-bg dark:bg-dark-bg rounded-lg">
                <MapPin size={16} className="text-light-subtle dark:text-dark-subtle" />
                <span className="text-sm text-light-text dark:text-dark-text">{service.studio_location}</span>
              </div>
            )}

            {service.virtual_meeting_link && (
              <div className="flex items-center gap-3 p-3 bg-light-bg dark:bg-dark-bg rounded-lg mt-2">
                <LinkIcon size={16} className="text-light-subtle dark:text-dark-subtle" />
                <a 
                  href={service.virtual_meeting_link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-primary hover:underline"
                >
                  {service.virtual_meeting_link}
                </a>
              </div>
            )}
          </div>
        )}

        {/* Add-ons */}
        {Array.isArray(service.addons) && service.addons.length > 0 && (
          <div className="bg-light-card dark:bg-dark-card rounded-xl p-5 shadow-sm">
            <h3 className="font-semibold text-sm mb-4 text-light-text dark:text-dark-text flex items-center gap-2">
              <Package size={16} />
              Add-ons
            </h3>
            <div className="space-y-3">
              {service.addons.map((addon, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-light-bg dark:bg-dark-bg rounded-lg">
                  <span className="font-medium text-sm text-light-text dark:text-dark-text">{addon.name}</span>
                  <span className="font-bold text-sm" style={{ color: '#4F46E5' }}>
                    KES {addon.price.toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Packages */}
        {Array.isArray(service.packages) && service.packages.length > 0 && (
          <div className="bg-light-card dark:bg-dark-card rounded-xl p-5 shadow-sm">
            <h3 className="font-semibold text-sm mb-4 text-light-text dark:text-dark-text">Packages</h3>
            <div className="space-y-3">
              {service.packages.map((pkg, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-light-bg dark:bg-dark-bg rounded-lg">
                  <div>
                    <span className="font-medium text-sm text-light-text dark:text-dark-text">{pkg.name}</span>
                    <span className="text-xs text-light-subtle dark:text-dark-subtle ml-2">
                      {pkg.sessions} sessions
                    </span>
                  </div>
                  <span className="font-bold text-sm" style={{ color: '#4F46E5' }}>
                    KES {pkg.price.toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tags */}
        {Array.isArray(service.tags) && service.tags.length > 0 && (
          <div className="bg-light-card dark:bg-dark-card rounded-xl p-5 shadow-sm">
            <h3 className="font-semibold text-sm mb-3 text-light-text dark:text-dark-text flex items-center gap-2">
              <Tag size={16} />
              Tags
            </h3>
            <div className="flex flex-wrap gap-2">
              {service.tags.map((tag, index) => (
                <span
                  key={index}
                  className="px-3 py-1.5 rounded-full text-xs font-medium bg-primary/10 dark:bg-primary/20 text-primary dark:text-golden"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Edit Button */}
        <button
          onClick={() => navigate(`/business/${businessId}/services/edit/${serviceId}`)}
          className="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-xl text-white font-semibold transition-all hover:opacity-90"
          style={{ backgroundColor: '#d1a02f' }}
        >
          <Edit size={20} />
          Edit Service
        </button>
      </div>
    </div>
  );
};

export default ServiceDetails;